import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import "@/App.css";
import { SoundProvider } from "@/hooks/useSound";
import { StudentAuthProvider } from "@/context/StudentAuth";
import { PickCursor } from "@/components/site/PickCursor";
import { FretRail } from "@/components/site/FretRail";
import { MobileBookBar } from "@/components/site/MobileBookBar";
import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { TopicsMarquee } from "@/components/site/TopicsMarquee";
import { About } from "@/components/site/About";
import { Courses } from "@/components/site/CoursesSection";
import { Media } from "@/components/site/Media";
import { Testimonials } from "@/components/site/Testimonials";
import { Faq } from "@/components/site/Faq";
import { Contact } from "@/components/site/ContactBooking";
import { Footer } from "@/components/site/Footer";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Profile from "@/pages/Profile";

const FretDivider = () => (
  <div className="max-w-[1600px] mx-auto px-6 md:px-12">
    <div className="fret-line relative">
      {[15, 38, 62, 85].map((p) => (
        <span
          key={p}
          className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#950606]/60"
          style={{ left: `${p}%`, boxShadow: "0 0 8px rgba(149,6,6,0.5)" }}
        />
      ))}
    </div>
  </div>
);

const StringsBackground = () => (
  <div aria-hidden className="fixed inset-0 pointer-events-none z-0">
    <div
      className="absolute inset-0 spotlight"
      style={{
        background:
          "radial-gradient(ellipse 55% 40% at 72% 6%, rgba(219,9,9,0.08), transparent 65%), radial-gradient(ellipse 50% 40% at 12% 72%, rgba(219,9,9,0.06), transparent 60%)",
      }}
    />
  </div>
);

const ScrollString = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return (
    <motion.div
      data-testid="scroll-progress-string"
      className="fixed top-0 left-0 right-0 h-[2.5px] origin-left z-[60] bg-[#DB0909]"
      style={{ scaleX, boxShadow: "0 0 8px rgba(219,9,9,0.45)" }}
    />
  );
};

const Landing = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.055, smoothWheel: true, wheelMultiplier: 0.9 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    // If arriving with a #hash (e.g., /#courses from Profile), scroll to it once Lenis is ready
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const scroll = () => {
        const el = document.querySelector(hash);
        if (el) lenis.scrollTo(el, { offset: -60, duration: 1.2 });
      };
      // Wait for the section to render (Media component loads videos, etc.)
      setTimeout(scroll, 250);
    }
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <SoundProvider>
      <div className="App grain">
        <StringsBackground />
        <ScrollString />
        <PickCursor />
        <FretRail />
        <MobileBookBar />
        <Navbar />
        <main className="relative z-10">
          <Hero />
          <TopicsMarquee />
          <About />
          <FretDivider />
          <Courses />
          <FretDivider />
          <Media />
          <FretDivider />
          <Testimonials />
          <FretDivider />
          <Faq />
          <FretDivider />
          <Contact />
        </main>
        <Footer />
      </div>
    </SoundProvider>
  );
};

function App() {
  return (
    <StudentAuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/profile" element={<Profile />} />
        </Routes>
      </BrowserRouter>
    </StudentAuthProvider>
  );
}

export default App;
